import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { fmtQty } from '@/features/manufacturing/planning/engine';
import { describeLeadTime } from '@/features/manufacturing/planning/leadTimes';
import type { ItemPlan, PlanningModel } from '@/features/manufacturing/planning/types';
import { PO_STATUS_LABELS, type PoStatus } from '@/features/procurement/hooks';
import { formatCurrency, formatPercent } from '@/lib/formatters';
import { formatQtyUom, formatUnitCostCents } from '@/lib/uom';
import { useIsMobile } from '@/lib/useMediaQuery';
import { cn } from '@/lib/utils';
import { GAP_SEVERITY_VARIANT, InlineStat, OrderStatusBadge, SectionTitle, orderQty, shortDate } from './shared';

interface ItemPlanSheetProps {
  plan: ItemPlan | null;
  model: PlanningModel;
  onOpenChange: (open: boolean) => void;
}

/** Day-by-day projected balance: with open POs only, and once suggestions land. */
function ProjectionChart({ plan }: { plan: ItemPlan }) {
  const { points, zeroY, safetyY, width, height, runoutX } = useMemo(() => {
    const width = 600;
    const height = 160;
    const pad = 8;
    const timeline = plan.timeline;
    const values = timeline.flatMap((t) => [t.balance, t.plannedBalance]);
    const max = Math.max(plan.safetyStockQty, ...values, 1);
    const min = Math.min(0, ...values);
    const span = max - min || 1;
    const x = (i: number) => pad + (i / Math.max(1, timeline.length - 1)) * (width - pad * 2);
    const y = (v: number) => pad + ((max - v) / span) * (height - pad * 2);
    const line = (key: 'balance' | 'plannedBalance') =>
      timeline.map((t, i) => `${x(i).toFixed(1)},${y(t[key]).toFixed(1)}`).join(' ');
    const runoutIdx = plan.runoutDate ? timeline.findIndex((t) => t.date === plan.runoutDate) : -1;
    return {
      width,
      height,
      points: { balance: line('balance'), planned: line('plannedBalance') },
      zeroY: y(0),
      safetyY: plan.safetyStockQty > 0 ? y(plan.safetyStockQty) : null,
      runoutX: runoutIdx >= 0 ? x(runoutIdx) : null,
    };
  }, [plan]);

  return (
    <div className="space-y-2">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-40 w-full overflow-visible" role="img" aria-label="Projected stock balance">
        <line x1={0} x2={width} y1={zeroY} y2={zeroY} className="stroke-fresh-salmon-dark/60" strokeDasharray="3 3" />
        {safetyY != null && (
          <line x1={0} x2={width} y1={safetyY} y2={safetyY} className="stroke-muted-foreground/40" strokeDasharray="2 4" />
        )}
        {runoutX != null && <line x1={runoutX} x2={runoutX} y1={0} y2={height} className="stroke-fresh-salmon-dark/40" />}
        <polyline points={points.planned} fill="none" className="stroke-bright-moss-dark" strokeWidth={2} strokeDasharray="5 3" />
        <polyline points={points.balance} fill="none" className="stroke-info" strokeWidth={2} />
      </svg>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 bg-info" /> on hand + open POs
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 border-t-2 border-dashed border-bright-moss-dark" /> with suggested orders
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 border-t border-dashed border-fresh-salmon-dark" /> zero
        </span>
        {safetyY != null && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-4 border-t border-dotted border-muted-foreground" /> safety stock
          </span>
        )}
        <span className="ml-auto tabular-nums">
          {shortDate(plan.timeline[0]?.date)} → {shortDate(plan.timeline[plan.timeline.length - 1]?.date)}
        </span>
      </div>
    </div>
  );
}

export function ItemPlanSheet({ plan, model, onOpenChange }: ItemPlanSheetProps) {
  const isMobile = useIsMobile();
  const open = !!plan;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={isMobile ? 'bottom' : 'right'} className={cn(!isMobile && 'sm:max-w-2xl')}>
        {plan && (
          <>
            <SheetHeader>
              <div className="flex items-start justify-between gap-3 pr-6">
                <div className="min-w-0">
                  <SheetTitle className="truncate">{plan.item.name}</SheetTitle>
                  <SheetDescription className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs">{plan.item.sku}</span>
                    {plan.supplier ? <span>· {plan.supplier.vendorName}</span> : <span className="text-fresh-salmon-dark">· no supplier</span>}
                    <Link to={`/catalog/items/${plan.item.id}?tab=suppliers`} className="inline-flex items-center gap-0.5 text-info hover:underline">
                      Open item <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  </SheetDescription>
                </div>
                {plan.orders[0] && <OrderStatusBadge status={plan.orders[0].status} />}
              </div>
            </SheetHeader>

            <SheetBody className="space-y-6">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <InlineStat label="On hand" value={fmtQty(plan.onHand)} />
                <InlineStat
                  label="On order"
                  value={
                    plan.onOrderPlaced || plan.onOrderDraft
                      ? `${fmtQty(plan.onOrderPlaced)}${plan.onOrderDraft ? ` +${fmtQty(plan.onOrderDraft)} draft` : ''}`
                      : '—'
                  }
                />
                <InlineStat label={`Need (${model.settings.horizonDays}d)`} value={`${fmtQty(plan.grossRequirement)} · ${fmtQty(plan.avgDailyUsage)}/day`} />
                <InlineStat
                  label="Runs out"
                  value={plan.runoutDate ? shortDate(plan.runoutDate) : 'Not in horizon'}
                  tone={plan.runoutDate ? 'warn' : 'ok'}
                />
                <InlineStat
                  label="Ideal order date"
                  value={
                    plan.orders[0]
                      ? plan.orders[0].idealOrderDate
                        ? plan.orders[0].idealOrderDate < model.today
                          ? 'Today (overdue)'
                          : shortDate(plan.orders[0].idealOrderDate)
                        : 'Needs lead time'
                      : '—'
                  }
                  tone={plan.orders[0] && (plan.orders[0].status === 'late' || plan.orders[0].status === 'order_now') ? 'warn' : undefined}
                />
                <InlineStat
                  label="Last safe day"
                  value={plan.orders[0]?.latestOrderDate ? shortDate(plan.orders[0].latestOrderDate) : '—'}
                  tone={plan.orders[0]?.status === 'late' ? 'warn' : undefined}
                />
              </div>

              {plan.shortages.length > 0 && (
                <div className="rounded-lg border border-fresh-salmon/60 bg-fresh-salmon/10 px-4 py-3 text-sm">
                  <div className="font-medium text-fresh-salmon-dark">Production will be short</div>
                  {plan.shortages.map((s) => (
                    <p key={s.fromDate} className="mt-1 text-foreground">
                      {shortDate(s.fromDate)} → {shortDate(s.toDate)}: up to {fmtQty(s.maxShortQty)} {plan.item.baseUom !== 'each' ? plan.item.baseUom : 'units'} short over{' '}
                      {s.workingDays} working day{s.workingDays === 1 ? '' : 's'}.
                      {s.earliestArrival && ` Nothing ordered today lands before ${shortDate(s.earliestArrival)}.`}
                    </p>
                  ))}
                  <p className="mt-1 text-xs text-muted-foreground">
                    Options: expedite an open PO, pull stock from another location, or move the builds that use it.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <SectionTitle hint="projected balance over the horizon">Stock projection</SectionTitle>
                <ProjectionChart plan={plan} />
              </div>

              <div className="space-y-2">
                <SectionTitle hint={plan.orders.length ? `${plan.orders.length} suggested` : undefined}>Suggested orders</SectionTitle>
                {plan.orders.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {plan.grossRequirement > 0 ? 'Stock and open POs cover the plan.' : 'Nothing in the plan uses this part yet.'}
                  </p>
                ) : (
                  <ul className="divide-y divide-border rounded-lg border border-border">
                    {plan.orders.map((order) => (
                      <li key={order.id} className="space-y-1.5 px-4 py-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium tabular-nums">
                            {orderQty(order, plan.item.baseUom)}
                            {order.totalCents != null && (
                              <span className="ml-2 font-normal text-muted-foreground">
                                {formatCurrency(order.totalCents)}
                                {order.unitCostCents != null &&
                                  (order.supplier?.purchaseUom && order.supplier.unitsPerPurchaseUom !== 1
                                    ? ` · ${formatCurrency(Math.round(order.unitCostCents * order.supplier.unitsPerPurchaseUom))}/${order.supplier.purchaseUom}`
                                    : ` · ${formatUnitCostCents(order.unitCostCents)}/${plan.item.baseUom !== 'each' ? plan.item.baseUom : 'ea'}`)}
                              </span>
                            )}
                          </span>
                          <OrderStatusBadge status={order.status} />
                        </div>
                        <div className="grid gap-x-4 gap-y-0.5 text-xs text-muted-foreground sm:grid-cols-2">
                          <span>
                            Ideal order date: <span className="text-foreground">{shortDate(order.idealOrderDate)}</span>
                          </span>
                          <span>
                            Last safe day: <span className="text-foreground">{shortDate(order.latestOrderDate)}</span>
                          </span>
                          <span>
                            Needed by: <span className="text-foreground">{shortDate(order.needByDate)}</span>
                            {order.coversFromDate !== order.needByDate && ` (covers from ${shortDate(order.coversFromDate)})`}
                          </span>
                          <span>
                            Covers through: <span className="text-foreground">{shortDate(order.coverageEndDate)}</span>
                          </span>
                          <span>
                            Net need {fmtQty(order.netRequirement)}
                            {order.moqApplied &&
                              ` → MOQ ${formatQtyUom(order.supplier?.minOrderQty ?? 0, order.supplier?.purchaseUom ?? plan.item.baseUom)}`}
                            {order.multipleApplied &&
                              ` → multiples of ${formatQtyUom(order.supplier?.orderMultiple ?? 0, order.supplier?.purchaseUom ?? plan.item.baseUom)}`}
                          </span>
                          {order.arrivalIfOrderedToday && (
                            <span>
                              Ordered today → lands <span className="text-foreground">{shortDate(order.arrivalIfOrderedToday)}</span>
                            </span>
                          )}
                        </div>
                        {order.priceBreakOption && (
                          <div
                            className={cn(
                              'rounded-md px-3 py-2 text-xs',
                              order.priceBreakOption.recommended
                                ? 'bg-bright-moss/20 text-bright-moss-dark'
                                : 'bg-secondary text-muted-foreground',
                            )}
                          >
                            {order.priceBreakOption.recommended ? 'Cheaper overall: ' : 'Price break: '}
                            order {orderQty(order, plan.item.baseUom, order.priceBreakOption)} at{' '}
                            {order.supplier?.purchaseUom && order.supplier.unitsPerPurchaseUom !== 1
                              ? `${formatCurrency(Math.round(order.priceBreakOption.unitCostCents * order.supplier.unitsPerPurchaseUom))}/${order.supplier.purchaseUom}`
                              : `${formatUnitCostCents(order.priceBreakOption.unitCostCents)}/ea`}{' '}
                            (
                            {formatPercent(order.priceBreakOption.unitSavingsPct)} less per unit) ={' '}
                            {formatCurrency(order.priceBreakOption.totalCents)} total,{' '}
                            {order.priceBreakOption.extraCashCents <= 0
                              ? `${formatCurrency(-order.priceBreakOption.extraCashCents)} less cash`
                              : `${formatCurrency(order.priceBreakOption.extraCashCents)} more cash`}
                            {order.priceBreakOption.extraCoverageDays > 0 && `, +${order.priceBreakOption.extraCoverageDays} working days of stock`}.
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="space-y-2">
                <SectionTitle>Lead time</SectionTitle>
                <p className="text-sm">{describeLeadTime(plan.leadTime)}</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground sm:grid-cols-4">
                  <div>
                    <div className="uppercase tracking-wider text-[10px]">Observed</div>
                    <div className="text-foreground">
                      {plan.leadTime.observedSamples > 0
                        ? `${plan.leadTime.observedMinDays}–${plan.leadTime.observedMaxDays}d, median ${plan.leadTime.observedMedianDays}d`
                        : 'No receipts yet'}
                    </div>
                    {plan.leadTime.observedLatestReceipt && <div>last {shortDate(plan.leadTime.observedLatestReceipt)}</div>}
                  </div>
                  <div>
                    <div className="uppercase tracking-wider text-[10px]">Vendor quote</div>
                    <div className="text-foreground">{plan.leadTime.quotedDays != null ? `${plan.leadTime.quotedDays}d` : '—'}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wider text-[10px]">Catalog worst case</div>
                    <div className="text-foreground">{plan.leadTime.catalogWorstDays != null ? `${plan.leadTime.catalogWorstDays}d` : '—'}</div>
                    {plan.item.leadTimeText && <div className="truncate">“{plan.item.leadTimeText}”</div>}
                  </div>
                  <div>
                    <div className="uppercase tracking-wider text-[10px]">Buffers</div>
                    <div className="text-foreground">
                      +{model.settings.safetyBufferDays}d early, +{model.settings.receivingBufferDays}d receiving
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <SectionTitle hint={plan.supplies.length ? `${plan.supplies.length} line${plan.supplies.length === 1 ? '' : 's'}` : undefined}>
                  Open purchase orders
                </SectionTitle>
                {plan.supplies.length === 0 ? (
                  <p className="text-sm text-muted-foreground">None open for this part.</p>
                ) : (
                  <ul className="divide-y divide-border rounded-lg border border-border text-sm">
                    {plan.supplies.map((s) => (
                      <li key={s.poLineId} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                        <div className="min-w-0">
                          <Link to="/procurement/purchase-orders" className="font-mono text-xs hover:underline">
                            {s.poNumber}
                          </Link>
                          <span className="ml-2 text-muted-foreground">{s.vendorName}</span>
                          <Badge variant={s.isDraft ? 'muted' : 'secondary'} className="ml-2">
                            {PO_STATUS_LABELS[s.status as PoStatus] ?? s.status}
                          </Badge>
                        </div>
                        <div className="text-right tabular-nums">
                          <div>{fmtQty(s.remainingQty)} due</div>
                          <div className={cn('text-xs', s.isOverdue ? 'text-fresh-salmon-dark' : 'text-muted-foreground')}>
                            {s.isDraft ? 'if placed: ' : s.isOverdue ? 'overdue, assume ' : ''}
                            {shortDate(s.arrivalDate)}
                            {s.arrivalAssumed && !s.isDraft && !s.isOverdue ? ' (est.)' : ''}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {plan.usedIn.length > 0 && (
                <div className="space-y-2">
                  <SectionTitle>Used in</SectionTitle>
                  <ul className="flex flex-wrap gap-2 text-sm">
                    {plan.usedIn.map((u) => {
                      const parent = model.itemsById.get(u.itemId);
                      return (
                        <li key={u.itemId} className="rounded-full border border-border px-3 py-1">
                          {parent?.item.name ?? 'Assembly'}
                          <span className="ml-1 text-xs text-muted-foreground">× {fmtQty(u.quantityPer)}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {plan.gaps.length > 0 && (
                <div className="space-y-2">
                  <SectionTitle>Missing information</SectionTitle>
                  <ul className="space-y-1.5 text-sm">
                    {plan.gaps.map((gap) => (
                      <li key={gap.id} className="flex items-start gap-2">
                        <Badge variant={GAP_SEVERITY_VARIANT[gap.severity]} className="mt-0.5 shrink-0">
                          {gap.severity}
                        </Badge>
                        <span>
                          <span className="font-medium">{gap.title}</span>
                          <span className="block text-xs text-muted-foreground">{gap.detail}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
